import { TApplicantSchematicFile } from "../../hooks/schematic-plan.hooks";

type Props = {
  /** Which of the two Offline Tentative choices is shown. */
  mode: "applicant_file" | "none";
  /** The applicant's Schematic Plan document, or null when none was uploaded. */
  applicantFile: TApplicantSchematicFile | null;
  /** False when the choice was made before this level (it is shown, not changed). */
  canChoose: boolean;
  isSaving: boolean;
  onBack: () => void;
  onNext: () => void;
};

/**
 * The body of the Schematic Plan step for the two choices only an Offline
 * Tentative application gets:
 * - "Use applicant's schematic": shows the file the applicant uploaded with
 *   the application; the certificate carries its pages after page 1.
 * - "No schematic plan": the certificate is issued without the schematic.
 * The choice itself is saved by SchematicPlanStep when Next is pressed.
 */
export function OfflineSchematicChoicePanel({
  mode,
  applicantFile,
  canChoose,
  isSaving,
  onBack,
  onNext,
}: Props) {
  const missingApplicantFile = mode === "applicant_file" && !applicantFile;

  return (
    <div className="p-6">
      <h2 className="text-[#423F3F] text-xl font-bold mb-1">Schematic Plan</h2>
      {!canChoose && (
        <p className="text-sm text-gray-600 mb-4">
          This choice was made before your level. You can continue with it.
        </p>
      )}

      {mode === "applicant_file" ? (
        applicantFile ? (
          <div className="border border-gray-300 rounded-lg p-4 max-w-2xl">
            <p className="text-sm text-gray-600 mb-1">
              The certificate will use the Schematic Plan the applicant uploaded:
            </p>
            <p className="font-semibold text-[#423F3F] break-words">{applicantFile.name}</p>
            <a
              href={applicantFile.url}
              target="_blank"
              rel="noreferrer"
              className="inline-block mt-2 text-blue-600 underline"
            >
              View file
            </a>
          </div>
        ) : (
          <p className="text-red-600 font-semibold">
            The applicant did not upload a Schematic Plan. Choose another option.
          </p>
        )
      ) : (
        <p className="text-gray-700 max-w-2xl">
          The certificate will be issued without a schematic plan.
        </p>
      )}

      <div className="flex flex-col sm:flex-row justify-center gap-3 mt-6">
        <button
          type="button"
          className="bg-black text-white px-10 py-2 grow md:grow-0 rounded-full font-bold"
          onClick={onBack}
        >
          Back
        </button>
        <button
          type="button"
          className="bg-primary px-10 py-2 grow md:grow-0 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
          disabled={isSaving || missingApplicantFile}
          onClick={onNext}
        >
          Next
        </button>
      </div>
    </div>
  );
}
